/* istanbul ignore file */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Center, HStack, Spinner, Stack, useDisclosure } from '@chakra-ui/react';
import {
  FieldType,
  FormVersionStatus,
  Property,
  RouterTargetType,
  SystemTranslationKeys,
} from '@common/entities';
import { useQuery } from '@tanstack/react-query';
import { RootState } from 'state/store';

import LanguageSwitcher from 'components/header/LanguageSwitcher';
import { TargetApplicationPropertyModal } from 'components/modal/TargetApplicationPropertyModal';
import { useSystemTranslation } from 'hooks';
import { useRouteTranslations } from 'hooks/useRouteTranslations';
import { getRoute } from 'services';

import { RenderRouteField } from './RenderRouteField';
import { RouteApplicationPreviewCard } from './RouteApplicationPreviewCard';

export interface Version {
  status: string;
}

interface Props {
  route?: any;
  routeId?: string;
  versionId?: string;
  isPreviewMode?: boolean;
  applicationSelected: (applicationId?: string) => void;
  setSelectedApplicationId: (id: string) => void;
  setShowLoginLink: (showLoginLink: boolean) => void;
}
export function RenderRoute(props: Props) {
  const {
    route,
    routeId,
    versionId,
    isPreviewMode = false,
    applicationSelected,
    setSelectedApplicationId,
    setShowLoginLink,
  } = props;

  const location = useLocation();

  const { orgId, propertyId } = useParams();
  const [targetId, setTargetId] = React.useState('');
  const [targetType, setTargetType] = React.useState('');
  const [targetName, setTargetName] = React.useState('');
  const [currentSequence, setCurrentSequence] = React.useState(1);
  const [navigationStack, setNavigationStack] = React.useState<string[]>([]);
  const [showApplicationPreviewCard, setShowApplicationPreviewCard] = React.useState(false);
  const [targetProperties, setTargetProperties] = React.useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = React.useState('');
  const [componentKey, setComponentKey] = React.useState(0);
  const { tSystem } = useSystemTranslation();
  const localeCode = useSelector((state: RootState) => state.locale.localeCode);
  const effectiveRouteId = routeId ?? route?.id;
  const effectiveVersionId =
    versionId ??
    route?.versions?.find((version: Version) => version.status === FormVersionStatus.Active)?.id;

  const navigate = useNavigate();
  useRouteTranslations(effectiveRouteId, effectiveVersionId, localeCode);

  const {
    isOpen: isPropertySelectionOpen,
    onOpen: onPropertySelectionOpen,
    onClose: onPropertySelectionClose,
  } = useDisclosure();

  const goToFirstQuestion = (section: any) => {
    const firstQuestion = section.elements.find((element: any) => element.sequence === 2);
    navigationStack.push(firstQuestion.id);
    setSequenceandLoginLink(2);
  };

  const goToNextStep = (section: any) => {
    if (targetId) {
      if (!navigationStack.includes(targetId)) {
        navigationStack.push(targetId);
      }

      if (targetType === RouterTargetType.Field) {
        const field = section.elements.find((element: any) => element.id === targetId);
        setSequenceandLoginLink(field.sequence);
      }
      if (targetType === RouterTargetType.Application) {
        if (isPreviewMode) {
          setShowApplicationPreviewCard(true);
        } else {
          const property = targetProperties.find(
            (prop: Property) => prop.propertyId === Number(propertyId),
          );
          if (property) {
            prepareApplicationLaunch();
          } else {
            processTargetProperties();
          }
        }
      }
    }
  };

  const prepareApplicationLaunch = () => {
    // Pass targetId so callers don't read stale state in the same tick (DEV-284170).
    setSelectedApplicationId(targetId);
    setShowLoginLink(true);
    applicationSelected(targetId);
  };

  const goToPreviousStep = (section: any) => {
    setShowApplicationPreviewCard(false);
    if (navigationStack.length === 1) {
      goToStartOfRoute();
      return;
    }
    navigationStack.pop(); // Remove the current targetId from the stack.
    const previousTargetId = navigationStack[navigationStack.length - 1];
    if (previousTargetId) {
      const previousField = section.elements.find(
        (element: any) => element.id === previousTargetId,
      );
      setTargetState(previousField.id, previousField.elementType, previousField.name);
      setSequenceandLoginLink(previousField.sequence);
      setComponentKey((prev) => prev + 1);
    }
  };

  const goToStartOfRoute = () => {
    setTargetState('', '', '');
    setNavigationStack([]);
    setSequenceandLoginLink(1);
  };

  const setTargetState = (
    targetIdState: string,
    targetTypeState: string,
    targetNameState: string,
    targetPropertiesState?: Property[],
  ) => {
    setTargetId(targetIdState);
    setTargetType(targetTypeState);
    setTargetName(targetNameState);
    setTargetProperties(targetPropertiesState ?? []);
  };

  const setSequenceandLoginLink = (sequence: number) => {
    setCurrentSequence(sequence);
    setShowLoginLink(sequence === 1);
  };

  const closePropertySelection = (action: string) => {
    if (action === 'Next') {
      onPropertySelectionClose();
      navigateToTargetApplication(Number(selectedPropertyId));
    } else {
      onPropertySelectionClose();
      navigationStack.pop();
    }
  };

  const { data: routeDefinition, isPending } = useQuery({
    queryKey: ['get-route', effectiveRouteId, effectiveVersionId],
    queryFn: () =>
      getRoute({
        applicationId: effectiveRouteId,
        versionId: effectiveVersionId,
      }),
  });

  if (isPending) return <Spinner />;

  const activeRouteVersion = routeDefinition?.data.versions[0];
  const section = activeRouteVersion?.form.pages[0].sections[0];
  const routeProperties = routeDefinition?.data.properties;
  const routeClientId = routeDefinition?.data.clientId;
  const previewLanguagePropertyId = routeProperties?.[0]?.propertyId;
  const field = section.elements.find((element: any) => element.sequence === currentSequence);

  const processTargetProperties = () => {
    const commonProperties = routeProperties.filter((routeProp: Property) =>
      targetProperties.some(
        (targetProp: Property) => targetProp.propertyId === routeProp.propertyId,
      ),
    );

    if (commonProperties.length === 1) {
      navigateToTargetApplication(commonProperties[0].propertyId);
    } else if (commonProperties.length > 1) {
      setTargetProperties(commonProperties);
      onPropertySelectionOpen();
    } else if (targetProperties.length === 1) {
      navigateToTargetApplication(targetProperties[0].propertyId);
    } else {
      onPropertySelectionOpen();
    }
  };

  const navigateToTargetApplication = (propId: number) => {
    const isSignup = location.pathname.endsWith('/signup');
    const pathSuffix = isSignup ? '/signup' : '/';

    navigate(`/${orgId}/${propId}${pathSuffix}${location.search}`);
    prepareApplicationLaunch();
  };

  const isRoutePreviewEmbedded = typeof window !== 'undefined' && window.self !== window.top;

  return (
    <Stack spacing={6} width="100%" maxW="600px">
      {isPreviewMode &&
        !isRoutePreviewEmbedded &&
        routeClientId != null &&
        routeClientId > 0 &&
        previewLanguagePropertyId != null &&
        previewLanguagePropertyId > 0 && (
          <LanguageSwitcher
            orgIdOverride={routeClientId}
            propertyIdOverride={previewLanguagePropertyId}
          />
        )}
      {showApplicationPreviewCard ? (
        <RouteApplicationPreviewCard applicationName={targetName} />
      ) : (
        <RenderRouteField
          key={componentKey}
          field={field}
          setTargetId={setTargetId}
          setTargetType={setTargetType}
          setTargetName={setTargetName}
          setTargetProperties={setTargetProperties}
        />
      )}
      {currentSequence === 1 && (
        <Center>
          <Button
            data-testid="route-start"
            variant="brand"
            marginTop={8}
            size="lg"
            width={300}
            onClick={() => {
              goToFirstQuestion(section);
            }}
          >
            {tSystem(SystemTranslationKeys.nav.continue)}
          </Button>
        </Center>
      )}
      {currentSequence > 1 && (
        <Center>
          <HStack>
            <Button
              data-testid="route-back"
              marginTop={8}
              marginRight={50}
              size="lg"
              width={75}
              onClick={() => {
                goToPreviousStep(section);
              }}
            >
              {tSystem(SystemTranslationKeys.nav.back)}
            </Button>
            <Button
              data-testid="route-next"
              variant="brand"
              marginTop={8}
              size="lg"
              width={75}
              onClick={() => {
                goToNextStep(section);
              }}
              isDisabled={field.type === FieldType.StaticText || showApplicationPreviewCard}
              _disabled={{ bg: 'gray.400', cursor: 'not-allowed' }}
            >
              {tSystem(SystemTranslationKeys.nav.next)}
            </Button>
          </HStack>
        </Center>
      )}

      <TargetApplicationPropertyModal
        isOpen={isPropertySelectionOpen}
        onNext={() => closePropertySelection('Next')}
        onBack={() => closePropertySelection('Back')}
        properties={targetProperties}
        setSelectedPropertyId={setSelectedPropertyId}
      />
    </Stack>
  );
}
